import { motion } from "framer-motion";
import { Star, Quote } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";

const EASE = [0.16, 1, 0.3, 1];

const REVIEWS = [
  {
    quote:
      "My daughter asks for 'the clever hare' every single night. The art is so lush she traces the leaves with her finger — and she's quietly absorbing the morals too.",
    name: "Priya M.",
    role: "Mother of two · London",
    featured: true,
  },
  {
    quote:
      "I read The Elephants and the Mice to my Year-1 class and the room went silent — then erupted. A rare book that teaches kindness without preaching it.",
    name: "Daniel Okafor",
    role: "Primary School Teacher · Manchester",
  },
  {
    quote:
      "Leemos la edición en español y luego la inglesa. Same story, two languages — my son switches between them like magic. Perfect for our bilingual home.",
    name: "Sofía R.",
    role: "Bilingual mamá · Madrid",
  },
  {
    quote:
      "Finally, screen-quality art in a printed book. Banyan Tree Press understands exactly how this generation of children sees the world.",
    name: "Prof. Anita Deshmukh",
    role: "Early-Childhood Educator · Mumbai",
  },
];

const Stars = () => (
  <div className="flex gap-1" aria-label="Five star review" role="img">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} className="h-4 w-4 fill-gold text-gold" aria-hidden="true" />
    ))}
  </div>
);

export default function Reviews() {
  const [featured, ...rest] = REVIEWS;
  return (
    <section
      id="reviews"
      data-testid="reviews-section"
      className="relative overflow-hidden bg-parchment py-24 text-charcoal lg:py-32"
    >
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(900px 500px at 100% 0%, rgba(212,175,55,0.14), transparent 60%)",
        }}
        aria-hidden="true"
      />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          testId="reviews-heading"
          eyebrow="Reader Reviews"
          title={
            <>
              Loved at Bedtime, <em className="text-emerald">Trusted in Classrooms.</em>
            </>
          }
          sub="Parents and educators across four continents on why the Panchatantra Tales have become family rituals."
        />

        <div className="mt-16 grid gap-8 lg:grid-cols-[1.1fr_1fr]">
          <motion.figure
            initial={{ opacity: 0, y: 36 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: EASE }}
            className="relative flex flex-col justify-between border border-emerald/15 bg-cream p-10 shadow-card sm:p-12"
            data-testid="review-card-featured"
          >
            <Quote className="absolute right-8 top-8 h-10 w-10 rotate-180 text-gold/25" aria-hidden="true" />
            <div>
              <Stars />
              <blockquote className="mt-6 font-serif text-2xl font-medium italic leading-relaxed text-charcoal sm:text-3xl">
                “{featured.quote}”
              </blockquote>
            </div>
            <figcaption className="mt-8 flex items-center gap-4">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald font-serif text-lg font-semibold text-gold">
                {featured.name.charAt(0)}
              </span>
              <div>
                <p className="font-semibold text-charcoal">{featured.name}</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-charcoal/55">
                  {featured.role}
                </p>
              </div>
            </figcaption>
          </motion.figure>

          <div className="grid gap-8 sm:grid-cols-1">
            {rest.map((r, i) => (
              <motion.figure
                key={r.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: 0.1 + i * 0.12, ease: EASE }}
                className="flex flex-col justify-between border border-emerald/15 bg-cream p-7 shadow-card transition-transform duration-300 hover:-translate-y-1 sm:p-8"
                data-testid={`review-card-${i + 2}`}
              >
                <div>
                  <Stars />
                  <blockquote className="mt-4 text-[15px] italic leading-relaxed text-charcoal/80">
                    “{r.quote}”
                  </blockquote>
                </div>
                <figcaption className="mt-5 flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald font-serif text-base font-semibold text-gold">
                    {r.name.charAt(0)}
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-charcoal">{r.name}</p>
                    <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-charcoal/55">
                      {r.role}
                    </p>
                  </div>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
